import { render, html } from "lit";
import { CellModel, ColumnSource, RequestFilters, RowSource, renderers } from "@omegagrid/grid";
import { dom, ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, FileManager, FileManagerEvent } from "@reporting/rp-app-host";
import { AccountingSettings } from "@reporting/rp-types/core";
import { BALANCE_TYPES, DATE_FORMATS, MONTH_FORMATS } from "../types";

const PAGE_SIZE = 500;

export class AccountingGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			filter: false,
			content: [{
				action: 'settings',
				title: msg('Settings'),
				icon: 'gear',
				color: ColorTypes.Gray
			}],
		}, {
			data: 'accounting',
			header: '#',
			t: 'n',
			e: false,
			w: 75,
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			e: true,
			levelButtons: true,
			levelIndent: true,
			w: 250,
		}, {
			data: 'fin_module_name',
			header: `${msg('Finance')} (F)`,
			t: 't',
			e: true,
			w: 115,
		}, {
			data: 'item_module_name',
			header: `${msg('Items')} (I)`,
			t: 't',
			e: true,
			w: 115,
		}, {
			data: 'balance_module_name',
			header: `${msg('Aging')} (A)`,
			t: 't',
			e: true,
			w: 115,
		}, {
			data: 'fin_begin_month',
			header: msg('Financial year'),
			t: 's',
			e: true,
			content: this.getFinancialYearList(),
			w: 140,
		}, {
			data: 'fin_report_period',
			header: msg('Reporting period'),
			t: 'b',
			e: true,
			w: 140,
		}, {
			data: 'item_balance_calculation',
			header: msg('Balance calculation method'),
			t: 's',
			e: true,
			content: BALANCE_TYPES,
			w: 200,
		}, {
			data: '_date_format',
			header: msg('Date format'),
			t: 's',
			e: true,
			content: DATE_FORMATS,
			w: 130,
			filter: false
		}, {
			data: '_date_format_month',
			header: msg('Month format'),
			t: 's',
			e: true,
			content: MONTH_FORMATS,
			w: 130,
			filter: false
		}, {
			data: 'mail_helpdesk',
			header: 'Email - Helpdesk',
			t: 't',
			e: true,
			w: 200,
		}, {
			data: 'mail_etl',
			header: 'Email - ETL',
			t: 't',
			e: true,
			w: 200,
		}, {
			data: 'mail_etl_file',
			header: `Email - ETL - ${msg('files')}`,
			t: 't',
			e: true,
			w: 200,
		}, {
			data: 'logo_url',
			header: 'Logo - Url',
			t: 't',
			e: true,
			w: 150,
		}, {
			data: 'id_logo',
			header: 'Logo',
			t: 't',
			e: false,
			renderer: this.logoRenderer,
			w: 100,
		}, {
			data: 'has_uid',
			header: msg('API'),
			t: 'b',
			e: false,
			renderer: this.apiRenderer,
			w: 140,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const settings = await rp.ms('accounting').get<AccountingSettings[]>(`settings`, this.createRequestParams(filters, {
			count: PAGE_SIZE,
			fields: [
				'accounting', 'company', 'fin_module_name', 'item_module_name', 'balance_module_name',
				'fin_begin_month', 'fin_report_period', 'item_balance_calculation', 'options', 'tree',
				'mail_helpdesk', 'mail_etl', 'mail_etl_file', 'logo_url', 'id_logo', 'idz', 'has_uid', 'level',
				'fin_flag', 'item_flag', 'balance_flag', 'is_admin',
			],
		}));
		return settings.map(data => ({
			l: data.level,
			e: data.is_admin === undefined ? undefined : !!data.is_admin,
			data: {
				...data,
				has_uid: !!data.has_uid,
				fin_module_name: data.fin_flag ? (data.fin_module_name ?? { dv: `<${msg('default')}>` }) : { dv: `<${msg('disabled')}>`, e: false },
				item_module_name: data.item_flag ? (data.item_module_name ?? { dv: `<${msg('default')}>` }) : { dv: `<${msg('disabled')}>`, e: false },
				balance_module_name: data.balance_flag ? (data.balance_module_name ?? { dv: `<${msg('default')}>` }) : { dv: `<${msg('disabled')}>`, e: false },
				_date_format: data.options?.date_format,
				_date_format_month: data.options?.date_format_month,
			}
		}));
	}

	getFinancialYearList(): Record<number, string>[] {
		const months = Array.from({ length: 12 }, (_, i) => i + 1);
		const currentYear = months.map(m => ({
			k: m,
			v: `${msg(`M${m}`)} - ${msg(`M${((m - 1) % 12) || 12}`)}`
		}));
		const prevYear = months.map((m, i) => ({
			k: 0 - i,
			v: `${msg(`M${m}`)} - ${msg(`M${((m - 1) % 12) || 12}`)} (Y-1)`
		}));
		return [...currentYear, ...prevYear];
	}

	logoRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(html`
			<og-button
				icon="pen"
				color="gray"
				style="height: 18px; width: 18px"
				title="${msg('Edit')}"
				@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
				@click="${() => this.onAction('fileManagerBtn', cell)}">
			</og-button>
			${cell.value ? html`
				<og-button
					icon="xmark"
					color="red"
					style="height: 18px; width: 18px;"
					title="${msg('Remove')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('removeBtn', cell)}">
				</og-button>
				<div style="flex: 1;">
					<img src="/ms/${rp.accountingProvider.a}/definition/files/${cell.value}/download" style="height: 18px; position: relative; top: 2px" />
				</div>
			` : ''}
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	apiRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.justifyContent = 'center';
		innerDiv.style.gap = '4px';
		innerDiv.style.minHeight = '22px';

		render(!cell.value ? html`
				<og-button
					icon="cloud-plus"
					color="blue"
					style="height: 18px; width: 100%;"
					text="${msg('Create key')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('createApiKey', cell)}">
				</og-button>
			` : html`
				<og-button
					icon="copy"
					color="yellow"
					style="height: 18px; width: 100%;"
					text="${msg('Copy key')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('copyApiKey', cell)}">
				</og-button>
			`
		, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			rowTree: false,
			columnHeaderTreeButtons: true,
			levelButtonCell: { r: -2, c: 0 },
			toolbar: {
				new: {enabled: false},
			},
			customToolbar: [{
				id: 'tb.groupSettings',
				type: 'button',
				text: msg('Group settings'),
				icon: 'gear',
				color: ColorTypes.Gray,
				disabled: !rp.userProvider.isGroupAdmin(),
			}],
		});
	}

	async save(rowData: RowData[]) {
		const data: AccountingSettings[] = rowData.map(row => ({
			...row.data,
			options: {
				date_format: row.data._date_format as string,
				date_format_month: row.data._date_format_month as string,
			}
		}));
		// do not use array in POST, because accounting must be set in RequestParams for each row
		for (const d of data) { await rp.ms('accounting').post(`settings`, d, { accounting: d.accounting }) }
	}

	async onAction(action: string, cell: CellModel) {
		const a = cell.row.data.accounting as number;
		const isAdmin = cell.row.data.is_admin as boolean;
		// the row buttons (settings, API key, logo) live in renderers and stay clickable on a
		// read-only ancestor row, so the guard belongs here too, not only on cell editability
		if (a && !isAdmin) return;

		switch (action) {
			case 'settings':
				if (a) rp.host.open(`users-settings-accounting-${a}`, { title: `${msg('Settings')}: ${cell.row.data.company}` });
				break;
			case 'tb.groupSettings':
				rp.host.open(`users-settings-group-${rp.accountingProvider.accounting.id_accounting_group}`, { title: msg('Group settings') });
				break;
			case 'fileManagerBtn':
				const component = dom.createElement<FileManager>('rp-definition-filemanager', {
					client: rp.ms('definition'),
				});
				component.addEventListener('filemanager.select', (e: FileManagerEvent) => {
					this.editor.setCellValue(cell.row.index, cell.column.index, e.file.id_file, e.file.filename);
				});
				rp.host.openModalSidebar({
					component,
					caption: `${msg('File Manager')} - Logo`,
					width: 600,
				});
				break;
			case 'createApiKey': // apiRenderer
				if (a) {
					const command = await rp.host.openMenu({
						content: 'yes_no',
						text: msg('Are you sure?')
					});
					if (command?.id == 'Y') await rp.ms('accounting').post('createUID', null, { accounting: a }).then(() => this.editor.refresh());
				}
				break;
			case 'copyApiKey': // apiRenderer
				if (a) {
					const uid = await rp.ms('accounting').get<{ uid: string }>('uid', { accounting: a });
					await navigator.clipboard.writeText(uid?.uid ?? '');
					rp.host.showAlert({ content: msg('Copied to clipboard'), type: 'info', title: msg('API') });
				}
				break;
			case 'removeBtn':
				this.editor.setCellValue(cell.row.index, cell.column.index, null);
				break;
		}
	}

}
